import Image from 'next/image';
import Router from 'next/router';
import { memo, useCallback } from 'react';

import { useTrackClickedPublicBoardLink } from '@air/analytics';
import { useAirModal } from '@air/provider-modal';

import { AssetModalHeaderAirLogoLink } from '~/components/AssetModalV2/Header/components/AssetModalHeaderAirLogoLink';
import { SignupModal } from '~/components/Modals/SignupModal/SignUpModal';
import { usePublicWorkspace } from '~/components/PublicWorkspaceProvider';
import { AccountInformation } from '~/components/v2/components/shared/account/AccountInformation';
import { WorkspaceAvatar } from '~/components/WorkspaceAvatarAndName';
import { usePublicCustomLogo } from '~/hooks/usePublicCustomLogo';
import { useShowPublicAirLogo } from '~/hooks/useShowPublicAirLogo';
import { useShowPublicSignUpButton } from '~/hooks/useShowPublicSignUpButton';
import { useAccountContext } from '~/providers/AccountProvider';

export const DefaultBanner = memo(() => {
  const { workspaceImage, workspaceName } = usePublicWorkspace();
  const { customLogoUrl } = usePublicCustomLogo();
  const { data: account } = useAccountContext();
  const [showSignUpModal] = useAirModal(SignupModal);
  const { trackClickedPublicBoardLink } = useTrackClickedPublicBoardLink();

  const { showAirLogo } = useShowPublicAirLogo();
  const { showSignUpButton } = useShowPublicSignUpButton();

  const onSignUpClick = useCallback(() => {
    showSignUpModal({
      ssoCallbackData: {
        url: Router.asPath,
      },
      accountCreationSource: 'share-link',
    });
    trackClickedPublicBoardLink({
      name: 'clicked login',
      page_area: 'banner',
      page_type: 'public board',
    });
  }, [showSignUpModal, trackClickedPublicBoardLink]);

  return (
    <div
      className="relative flex size-full h-12 items-center justify-between gap-8 ads:border-ads-gray-5 border-gray-5 border-b ads:bg-ads-gray-4 bg-gray-4 px-4"
      data-testid="PUBLIC_ASSET_DEFAULT_BANNER"
    >
      <div className="flex items-center gap-2" data-testid="PUBLIC_ASSET_WORKSPACE_INFO">
        {customLogoUrl ? (
          // Custom brand marks are frequently wide (horizontal wordmarks), so the logo renders
          // whole at the avatar slot's height with auto width instead of going through
          // WorkspaceAvatar's square crop (the fallback branch below keeps the square avatar).
          // Rendered locally rather than changing the shared WorkspaceAvatar, which private
          // surfaces use as a square avatar. max-w bracket is a proto-fidelity cap
          // (mattq/proto-presentation-mode) — no configured max-w steps exist near it.
          // `unoptimized` skips the next/image resizer, whose fixed-size renditions would soften
          // wide marks scaled by CSS; height/width are only the intrinsic-ratio hint.
          <Image
            alt={`Logo for ${workspaceName}`}
            className="h-8 w-auto max-w-[160px] shrink-0 object-contain object-left"
            data-testid="PUBLIC_CUSTOM_LOGO"
            height={32}
            src={customLogoUrl}
            unoptimized
            width={192}
          />
        ) : (
          <>
            {workspaceImage && <WorkspaceAvatar image={workspaceImage} name={workspaceName} />}
            <p className="truncate ads:text-metadata text-12 text-gray-10">{workspaceName}</p>
          </>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-4">
        {account ? (
          <AccountInformation />
        ) : (
          showSignUpButton && (
            <button
              className="cursor-pointer font-medium ads:text-secondary text-12 text-gray-11 hover:underline"
              data-testid="PUBLIC_VIEWER_SIGN_UP_CTA"
              onClick={onSignUpClick}
              type="button"
            >
              Sign up for Air
            </button>
          )
        )}

        {showAirLogo && (
          <>
            <div className="h-4 w-px ads:bg-ads-gray-5 bg-gray-5" />
            <AssetModalHeaderAirLogoLink />
          </>
        )}
      </div>
    </div>
  );
});

DefaultBanner.displayName = 'DefaultBanner';
